import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Film } from "lucide-react";
import { api, API, authHeader } from "@/lib/api";
import Lightbox from "@/components/Lightbox";

const isVideo = (ct) => ct && ct.startsWith("video/");

export const MediaGallery = ({ files, onDelete, editable, showCaptions = true }) => {
  const [openIdx, setOpenIdx] = useState(null);

  if (!files || files.length === 0) {
    return (
      <p className="label-eyebrow mt-4" style={{ color: "var(--senk-body)" }}>
        {editable ? "No files yet — upload images or videos for this phase." : "No media yet for this phase."}
      </p>
    );
  }

  const close = () => setOpenIdx(null);
  const prev = () => setOpenIdx((i) => (i === null ? null : (i - 1 + files.length) % files.length));
  const next = () => setOpenIdx((i) => (i === null ? null : (i + 1) % files.length));

  const saveCaption = async (id, value) => {
    try {
      await api.patch(`/admin/media/${id}`, { caption: value }, { headers: authHeader() });
    } catch {
      toast.error("Failed to save caption");
    }
  };

  return (
    <>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="milestone-media-grid">
        {files.map((f, idx) => {
          const url = `${API}/files/${f.id}`;
          return (
            <div key={f.id} className="relative group" data-testid={`milestone-media-${f.id}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(idx)}
                data-testid={`milestone-media-open-${f.id}`}
                className="w-full overflow-hidden block p-0 border-0 cursor-zoom-in"
                style={{ background: "#EEEDEA", borderRadius: 8 }}
              >
                {isVideo(f.content_type) ? (
                  <video src={url} preload="metadata" muted className="w-full h-44 object-cover pointer-events-none" />
                ) : (
                  <img src={url} alt="" className="w-full h-44 object-cover block" loading="lazy" />
                )}
              </button>
              {editable ? (
                <input
                  type="text"
                  defaultValue={f.caption || ""}
                  placeholder="Add caption (optional)"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (f.caption || "")) saveCaption(f.id, v);
                  }}
                  data-testid={`milestone-media-caption-${f.id}`}
                  className="senk-input mt-2"
                  style={{ fontSize: 13 }}
                />
              ) : (
                showCaptions && f.caption ? (
                  <p
                    data-testid={`milestone-media-caption-public-${f.id}`}
                    className="mt-2 text-center"
                    style={{
                      color: "#8A8680",
                      fontFamily: "DM Sans, sans-serif",
                      fontWeight: 400,
                      fontSize: "11px",
                      margin: "0.5rem 0 0",
                    }}
                  >
                    {f.caption}
                  </p>
                ) : null
              )}
              {editable && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDelete(f.id); }}
                  data-testid={`milestone-media-delete-${f.id}`}
                  className="absolute top-2 right-2 p-2 transition-opacity opacity-0 group-hover:opacity-100"
                  style={{ background: "var(--senk-yellow)", color: "var(--senk-yellow-fg)", borderRadius: 999 }}
                  aria-label="Delete file"
                >
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {openIdx !== null && (
        <Lightbox files={files} index={openIdx} onClose={close} onPrev={prev} onNext={next} />
      )}
    </>
  );
};

export const MilestoneMedia = ({ projectId, slug, milestoneIndex, editable, autoLoad = true, showCaptions = true }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = editable
        ? `/admin/projects/${projectId}/milestones/${milestoneIndex}/media`
        : `/portal/${slug}/milestones/${milestoneIndex}/media`;
      const res = await api.get(url, editable ? { headers: authHeader() } : {});
      setFiles(res.data);
    } catch {
      // Silent — empty state will render
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
  }, [projectId, slug, milestoneIndex, autoLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(
            `/admin/projects/${projectId}/milestones/${milestoneIndex}/media`,
            fd,
            { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }
          );
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
        }
      }
      await load();
      toast.success("Upload complete");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/admin/media/${id}`, { headers: authHeader() });
      setFiles((arr) => arr.filter((x) => x.id !== id));
      toast.success("File removed");
    } catch {
      toast.error("Could not remove file");
    }
  };

  return (
    <div className="mt-4">
      {editable && (
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={onPick}
            className="hidden"
            data-testid={`milestone-media-upload-input-${milestoneIndex}`}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn-ghost btn-type"
            data-testid={`milestone-media-upload-${milestoneIndex}`}
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={2} />}
            <span>Upload media</span>
          </button>
          <span className="label-eyebrow">Images & video · up to 50 MB</span>
        </div>
      )}
      {loading ? (
        <div className="py-6 flex items-center"><Loader2 className="animate-spin" size={16} /></div>
      ) : (
        <MediaGallery files={files} onDelete={onDelete} editable={!!editable} showCaptions={showCaptions} />
      )}
    </div>
  );
};

export default MilestoneMedia;
